import React, { forwardRef, useEffect, useImperativeHandle } from 'react';

import { useIntl } from 'umi';
import CardCommon, { CardCommonType } from '@op/components/CardCommon';
import { useRequestFeedsGpt } from '../hooks/useRequestGpt';
import { RequestFuncMapType } from '../hooks/useDefaultSummary';
import PromptModal from '../PromptModal';
import {
  copyIcon,
  fullIcon,
  refreshIcon,
  saveIcon,
  SymbolType,
} from '@op/constants/chart';
import { GptCardView, LeftElementType } from '../components/GptCardView';
import {
  FailedTipsTextType,
  LoadingTextType,
  PageCardRefType,
  PromptConfirmItemType,
  PromptListType,
  ScenarioType,
  GptRequestType,
} from '../types';
import '../styles/index.scss';
import usePageCardData from '../hooks/usePageCardData';
import { DBO_T } from '@op/utils/format';

type PageCardType = {
  // GPT的模式，主贴和评论
  scenario?: ScenarioType;
  // 过滤器条件，创建GPT的时候需要
  filter: any;
  // 返回GPT的内容
  backGPTText?: (value: string | GptRequestType) => void;
  // 停止的回调函数
  stopAction?: () => void;
  // 错误回调函数
  errorCallback?: () => void;
  // 成功的回调函数
  successAction?: () => void;
  // 返回state接口的数据
  backStateData?: (data: any) => void;
  // 是否拥有停止按钮
  hasStopBtn?: boolean;
  // 进行前置请求的函数接口以及处理方式
  requestFuncMap?: RequestFuncMapType;
  // 是否自动请求
  autoRequest?: boolean;
  // 外部获取gpt的loading状态
  getGptLoading?: (value: boolean) => void;
  // 获取gpt是否处于，get gpt summary按钮和计算的状态
  getGptShowChildren?: (value: boolean) => void;
  // 外部传入的gpt内容
  // gptDefaultHtml?: string;
  children: (
    open: (value?: boolean) => void,
    options: { loading?: boolean; total: number; handleStop: () => void },
  ) => any;
  // 外部的state数据
  oriFeedsState?: any;
  // 是否需要关联filter变化，默认为true
  linkFilterChange?: boolean;
  // 默认的prompt列表
  promptList?: PromptListType | ((wholeGameInfo: any) => PromptListType);
  // 始终显示标题
  alwaysShowTitle?: boolean;
  // prompt弹窗confirm操作
  promptConfirm?: (
    data: PromptConfirmItemType,
    defaultAction: null | (() => void),
  ) => boolean;
  // 是否有高级GPT的选项
  hasAdvancedSummary?: boolean;
  // loading状态下再stop按钮和进度条间添加一些元素
  loadingElement?: any;
  // 基础GPT报告是否先获取taskId,再进行回调
  needBasicTaskId?: boolean;
  // 保存事件，有保存事件，就会有保存的图标按钮
  saveClick?: () => void;
  // 左侧额外显示内容，那么GPT展示右侧，该元素展示在左侧
  leftElement?: LeftElementType;
  // 默认的prompt内容
  promptValue?: PromptConfirmItemType;
  // 是否展示Result Output选项
  hasResultOutput?: boolean;
  // prompt是否为预览模式
  previewPrompt?: boolean;
  // 状态组件是否为min模式
  isMini?: boolean;
  // Loading的文案配置
  loadingTipsText?: LoadingTextType;
  // 失败的文案
  failedTipsText?: FailedTipsTextType;
  // 隐藏图标列表
  hideIcons?: Array<SymbolType>;
  // hideSentiment
  hideSentiment?: boolean;
  // 是否要请求prompt数据
  needRequestPrompt?: boolean;
  hideFullSubTitle?: boolean;
} & Omit<CardCommonType, 'children'>;

/**
 * 当前页面展示GPT文案的卡片
 * @param param0
 * @returns
 */
function VideoPageCard(props: PageCardType, ref: PageCardRefType) {
  const {
    filter,
    backGPTText,
    stopAction,
    errorCallback,
    successAction,
    backStateData,
    hasStopBtn = false,
    requestFuncMap,
    autoRequest = false,
    children,
    title,
    gptSumaryEndSlot,
    getGptSummaryData,
    getGptLoading,
    getGptShowChildren,
    lessThanIsEmpty = 20,
    gameInfo: propsGameInfo,
    wholeGameInfo: propsWholeGameInfo,
    oriFeedsState,
    promptList,
    linkFilterChange,
    alwaysShowTitle = false,
    promptConfirm,
    hasAdvancedSummary,
    loadingElement,
    needBasicTaskId,
    saveClick,
    scenario,
    leftElement,
    promptValue,
    hasResultOutput,
    previewPrompt,
    isMini,
    loadingTipsText,
    failedTipsText,
    hideIcons = ['save'],
    hideSentiment = false,
    needRequestPrompt,
    hideFullSubTitle,
    hasBubble,
    ...cardProps
  } = props;

  const { formatMessage } = useIntl();

  const {
    setPromptInfo,
    promptInfo,
    visible,
    toggleVisible,
    FullDialog,
    openFullDialog,
    showChildren,
    showDefaultGptRef,
    showDefaultGpt,
    setShowDefaultGpt,
    loading,
    defaultGptText,
    defaultGPT,
    total,
    changeShowChildren,
    titleInfo,
  } = usePageCardData({
    filter,
    requestFuncMap,
    backStateData,
    oriFeedsState,
    getGptLoading,
    linkFilterChange,
    alwaysShowTitle,
    title,
    formatMessage,
  });

  const {
    status,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    requestGptData,
    summary,
    errorType,
    GPTSentiment,
  } = useRequestFeedsGpt({
    filter,
    stopAction,
    successAction,
    backGPTText,
    errorCallback,
    defaultGptText,
    showDefaultGpt,
    getGptSummaryData,
    lessThanIsEmpty,
    defaultGPT,
    autoRequest
  });

  function getPageSummary(
    data: PromptConfirmItemType,
    defaultPercent = 0,
  ) {
    setPromptInfo(data);
    requestGptData(data, defaultPercent);
    changeShowChildren(false);
  }

  useEffect(() => {
    if (showDefaultGptRef.current && !loading) {
      changeShowChildren(isEmpty);
    }
  }, [showDefaultGpt, isEmpty, loading]);

  function getOperations() {
    if (showChildren) return [];
    if (isEmpty && status === 'loading') return [];
    let list = [];
    if (isEmpty) {
      list = [refreshIcon];
    } else {
      list = [refreshIcon, copyIcon, saveIcon, fullIcon];
    }

    return list.filter((item) => !hideIcons.includes(item.symbol));
  }

  useImperativeHandle(ref, () => ({
    getPageSummary,
    handleStop,
    toggleVisible,
  }));

  return (
    <>
      <CardCommon
        className="dbo-aigc-card-wrapper"
        title={titleInfo}
        loading={loading}
        operations={getOperations()}
        logBtnId="GPT"
        operationInfo={['', () => {}]}
        handleOperationsOnClick={({ symbol }) => {
          if (symbol === 'copy') {
            DBO_T.copyText(summary, formatMessage);
          } else if (symbol === 'refresh') {
            if (isEmpty) changeShowChildren(true);
            else toggleVisible();
          } else if (symbol === 'fullscreen') {
            openFullDialog();
          } else if (symbol === 'save') {
            saveClick?.();
          }
        }}
        {...cardProps}
      >
        {/* !gptDefaultHtml && showChildren ? */}
        {/* showChildren为展示Get AI Summary 按钮  */}
        {showChildren ? (
          children(toggleVisible, { loading, total, handleStop })
        ) : (
          <GptCardView
            status={status}
            total={total}
            percent={percent}
            GPTSentiment={hideSentiment ? undefined : GPTSentiment}
            hasStopBtn={hasStopBtn}
            gptSumaryEndSlot={gptSumaryEndSlot}
            isLittle={total < 10}
            handleStop={() => {
              handleStop();
              changeShowChildren(true);
            }}
            reload={() => toggleVisible()}
            renderHtml={renderHtml}
            isEmpty={isEmpty}
            isMini={isMini}
            errorType={errorType}
            loadingElement={loadingElement}
            promptInfo={promptInfo}
            leftElement={leftElement}
            loadingTipsText={loadingTipsText}
            failedTipsText={failedTipsText}
            hasBubble={hasBubble}
          ></GptCardView>
        )}
      </CardCommon>

      <PromptModal
        visible={visible}
        studioGameInfo={propsGameInfo}
        studioWholeGameInfo={propsWholeGameInfo}
        promptList={promptList}
        hasAdvancedSummary={hasAdvancedSummary}
        needBasicTaskId={needBasicTaskId}
        close={() => toggleVisible()}
        scenario={scenario}
        needRequestPrompt={needRequestPrompt}
        promptValue={promptValue}
        previewPrompt={previewPrompt}
        hasResultOutput={hasResultOutput}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        handleConfirm={async (data, defaultAction) => {
          toggleVisible();
          setShowDefaultGpt(false);
          // 如果有自定义的promptConfirm，优先使用自定义的
          // 自定义返回true就不继续向后走
          const flag = await promptConfirm?.(data, defaultAction);
          if (flag) return;

          if (data.resultType === 'page') {
            getPageSummary(data);
          } else {
            defaultAction?.();
          }
        }}
        filter={filter}
      ></PromptModal>
      <FullDialog
        renderHtml={renderHtml}
        summary={summary}
        hideSubTitle={hideFullSubTitle}
        GPTSentiment={hideSentiment ? undefined : GPTSentiment}
      ></FullDialog>
    </>
  );
}

export default forwardRef<PageCardRefType, PageCardType>(VideoPageCard as any);
